import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { addMinutes } from 'date-fns';
import moment from 'moment';
import { StripePaymentModalComponent } from 'src/app/shared/modals/stripe-payment-modal/stripe-payment-modal.component';
import { BookingStatusEnum } from 'src/app/shared/models/booking-status.enum';
import { GtStripeService } from 'src/app/shared/services/gt-stripe.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { Simulator } from 'src/app/simulator-booking/models/simulators';
import { BookingService } from 'src/app/simulator-booking/services/booking.service';
import { BaseBookingModalComponent } from '../base-booking-modal.component';
import { AddonService } from 'src/app/simulator-booking/services/addon.service';
import { BookingAddon } from 'src/app/simulator-booking/models/booking-addon';
import { CreditCardFeeType } from 'src/app/shared/models/creditcard-fee-type.enum';
import { OperatingHours } from 'src/app/simulator-booking/models/operating-hours';

@Component({
    selector: 'booking-create-modal',
    templateUrl: './booking-create-modal.component.html',
    styleUrl: './booking-create-modal.component.scss',
    standalone: false
})
export class BookingCreateModalComponent extends BaseBookingModalComponent<BookingCreateModalComponent> implements OnInit, OnDestroy {

  public simulator: Simulator = new Simulator();
  public availableDurations: any[] = [];
  public bookingStatusEnum = BookingStatusEnum;
  public bookingStatusEnumKeys = Object.keys(BookingStatusEnum).filter(key => isNaN(Number(key)));
  private stripePaymentIntentData: any = {};
  private customerResponse: any;
  userDetails: any;
  operatingHours: OperatingHours[] = [];
  
  constructor(protected sessionService: SessionService,
    protected bookingService: BookingService,
    public dialogRef: MatDialogRef<BookingCreateModalComponent>,
    public stripeService: GtStripeService,
    protected addonService: AddonService,
    protected dialog: MatDialog) {
    super(sessionService, bookingService, addonService, dialogRef, dialog);
    this.form.get('organizationId').setValue(this.sessionService.getUser().organizationId);
    this.form.get('createdBy').setValue(this.sessionService.getUserId());
    this.operatingHours = JSON.parse(this.sessionService.getUser().operatingHours as unknown as string) as OperatingHours[];
    }

  ngOnInit(): void {
    this.form.get('startTime').setValue(moment(this.startTime).format('MM/DD/YYYY hh:mm A'));
    this.getBookingAvailability();
    this.priceOverrideSubscription = this.form.get('priceOverride').valueChanges.subscribe(value => {
      if (this.form.get('priceOverride').valid) {
        this.form.get('totalPrice').setValue(value);
      } else {
        this.form.get('totalPrice').setValue(this.availableDurations.length > 0 ? this.availableDurations[0].price : 0);
      }
    });
  }

  ngOnDestroy() {
    if (this.priceOverrideSubscription) {
      this.priceOverrideSubscription.unsubscribe();
    }
  }

  getBookingAvailability() {

    let currentDayOperatingHours = this.operatingHours.find(h => h.dayOfWeek === this.startTime.getDay());

    let operatingHoursCloseTime = '';
    if (currentDayOperatingHours) {
      if (currentDayOperatingHours.isNextDay) {
        // Set to midnight of the next day
        const nextDay = new Date(this.startTime);
        nextDay.setDate(nextDay.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);
        operatingHoursCloseTime = nextDay.toLocaleString();
      } else {
        // Use current day's close time
        const [closeHour, closeMinute] = currentDayOperatingHours.closeTime.split(':').map(Number);
        const closeDate = new Date(this.startTime);
        closeDate.setHours(closeHour, closeMinute, 0, 0);
        operatingHoursCloseTime = closeDate.toLocaleString();
      }
    }

    let data = {
      simulatorId: this.form.get('simulatorId').value,
      bookingDate: this.startTime.toLocaleString(),
      organizationId: this.sessionService.getUser().organizationId,
      timezone: this.userDetails.timeZone,
      operatingHoursCloseTime: operatingHoursCloseTime
    };

    this.isLoading = true;
    this.bookingService.getAvailableDurations(data).subscribe({
      next: (result) => {
        this.availableDurations = result;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error(error);
        this.setError(error, this.form);
      },
      complete: () => this.getAddons()
    });
  }

  setEndTimeAndTotalPrice(duration) {
    let endTime = addMinutes(this.form.get('startTime').value, duration.durationMinutes);
    this.form.get('endTime').setValue(endTime.toString());

    let totalPrice = this.userDetails.creditCardFeeType === CreditCardFeeType.Percentage ?
      duration.price * (1 + this.userDetails.creditCardFee / 100) :
      duration.price + this.userDetails.creditCardFee;

    this.form.get('totalPrice').setValue(totalPrice);
  }

  submit({ value, valid }) {
    if (valid) {
      this.isLoading = true;
      this.createBooking(value);
    }
  }

  createBooking(value) {
    const bookingAddons: BookingAddon[] = [];
    if (this.selectedAddons?.length > 0) {
      this.selectedAddons.forEach(addon => {
        const bookingAddon = new BookingAddon();
        bookingAddon.addonId = addon.id;
        bookingAddon.organizationId = this.sessionService.getUser().organizationId;
        bookingAddon.createdBy = this.sessionService.getUserId();
        bookingAddons.push(bookingAddon);
      });
    }

    value.addons = bookingAddons;
    value.startTime = this.stringHelpers.normalizeDateTime(value.startTime);
    value.endTime = this.stringHelpers.normalizeDateTime(value.endTime);
    value.timeZone = this.userDetails.timeZone;
    value.stripePaymentIntentId = this.stripePaymentIntentData.id;

    this.bookingService.create(value).subscribe({
      next: (result) => {
        if (result !== null || result !== undefined) {
          if (this.stripePaymentIntentData.hasPaid) {
            this.updateStripePaymentIntent(result);
          } else {
            this.isLoading = false;
            this.dialogRef.close("save");
          }
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error(error);
        this.setError(error, this.form);
      }
    });
  }

  onSubmitWithPayment() {
    this.isLoading = true;

    this.bookingService.createCustomer({
      accountId: this.userDetails.stripeAccountId,
      options: {
        email: this.form.get('email').value,
        name: this.form.get('customerName').value
      }
    }).subscribe({
      next: (result) => {
        this.customerResponse = result;
        this.submitWithPayment();
      },
      error: (err) => {
        this.isLoading = false;
        console.error(err);
      },
    });
  }

  submitWithPayment() {
    const dialogRef = this.dialog.open(StripePaymentModalComponent, {
      width: '50vw',
      height: 'auto',
      disableClose: true
    });

    dialogRef.componentInstance.stripePaymentIntentData = {
      options: {
        amount: this.form.get('totalPrice').value * 100,
        currency: 'usd',
        customer: this.customerResponse.customer.id,
        setup_future_usage: 'off_session',
        metadata: {
          CustomerName: this.form.get('customerName').value,
          CustomerPhone: this.form.get('phoneNumber').value,
          Auth0Id: this.sessionService.getUser().auth0Id,
          UserId: this.sessionService.getUser().id,
          Parent: 'true'
        }
      },
      requestOptions: {
        stripeAccount: this.sessionService.getUser().stripeAccountId
      }
    };

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result.status === 'success') {
          this.stripePaymentIntentData = result.paymentIntent;
          this.stripePaymentIntentData.hasPaid = true;
          this.form.get('hasPaid').setValue(true);
          this.createBooking(this.form.value);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error(err);
      },
      complete: () => this.isLoading = false
    });
  }

  updateStripePaymentIntent(booking) {
    let data = {
      paymentIntentId: this.stripePaymentIntentData.id,
      options: {
        metadata: {
          BookingId: booking.id,
          ConfirmatioNumber: booking.confirmatioNumber
        }
      },
      requestOptions: {
        stripeAccount: this.sessionService.getUser().stripeAccountId
      }
    };

    this.stripeService.updatePaymentIntent(data).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.dialogRef.close("save");
      },
      error: (err) => {
        this.isLoading = false;
        console.error(err);
      },
      complete: () => this.isLoading = false
    });
  }

  onAddonSelectionChange(event: any) {
    const previousAddons = this.selectedAddons || [];
    this.selectedAddons = event.value;

    // Always set the addons value, even if empty array
    this.form.get('addons').setValue(this.selectedAddons);

    // Get base price from duration
    let totalPrice = this.form.get('duration').value?.price || 0;

    // Calculate price difference from addon changes
    const removedAddons = previousAddons.filter(prev =>
      !this.selectedAddons.some(curr => curr.id === prev.id)
    );
    const addedAddons = this.selectedAddons.filter(curr =>
      !previousAddons.some(prev => prev.id === curr.id)
    );

    let currentTotal = this.form.get('totalPrice').value;
    removedAddons.forEach(addon => {
      currentTotal -= addon.price;
    });
    addedAddons.forEach(addon => {
      currentTotal += addon.price;
    });

    this.form.get('totalPrice').setValue(currentTotal);
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `${hours} hours ${remainingMinutes} minutes`;
  }
}